import { ShipTierColors, ShipTierNames } from "@voidex/contracts-ts";
import { useEffect, useState } from "react";
import { useReadContract } from "wagmi";
import { type ActiveFarmSession, useClaimFarm } from "../../hooks";
import { formatCountdown, getZoneColor } from "../../services/zoneUtils";
import { contractConfigs } from "../../web3/contracts";
import { HudDivider } from "../shared/HudDivider";
import { LoadingButton } from "../shared/LoadingButton";
import { ShipIcon } from "../shared/ShipIcon";
import { ClaimAnimationModal } from "./ClaimAnimationModal";

interface ActiveMissionsProps {
	sessions: ActiveFarmSession[];
	isLoading: boolean;
}

export function ActiveMissions({ sessions, isLoading }: ActiveMissionsProps) {
	if (isLoading) {
		return (
			<div className="active-missions">
				<HudDivider label="ACTIVE MISSIONS" />
				<div className="active-missions__loading">
					<div className="loading-spinner" />
					<span>Loading missions...</span>
				</div>
			</div>
		);
	}

	if (sessions.length === 0) return null;

	return (
		<div className="active-missions">
			<HudDivider label="ACTIVE MISSIONS" count={sessions.length} />
			<div className="mission-terminal">
				<div className="mission-terminal__header">
					<span>Ship</span>
					<span>Zone</span>
					<span>Status</span>
					<span>ETA</span>
					<span />
				</div>
				{sessions.map((session) => (
					<MissionRow key={session.sessionId.toString()} session={session} />
				))}
			</div>
		</div>
	);
}

function MissionRow({ session }: { session: ActiveFarmSession }) {
	const tierName = ShipTierNames[session.shipTier];
	const tierColor = ShipTierColors[session.shipTier];

	// Read on-chain session data for V2 fields
	const { data: sessionData } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "getSession",
		args: [session.sessionId],
		query: { refetchInterval: 10_000 },
	});

	// Read claimable status
	const { data: claimable } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "isClaimable",
		args: [session.sessionId],
		query: { refetchInterval: 10_000 },
	});

	const { claimFarm, isPending: isClaiming, isSuccess: claimSuccess } = useClaimFarm();
	const [showClaimAnim, setShowClaimAnim] = useState(false);

	// Show extraction animation when claim tx confirms
	useEffect(() => {
		if (claimSuccess) setShowClaimAnim(true);
	}, [claimSuccess]);

	// Extract V2 fields from on-chain data
	const vrfDone = sessionData ? (sessionData as { vrfDone: boolean }).vrfDone : false;
	const farmDuration = sessionData
		? Number((sessionData as { farmDuration: bigint }).farmDuration)
		: 0;
	const startedAt = sessionData ? Number((sessionData as { startedAt: bigint }).startedAt) : 0;
	const zone = sessionData ? Number((sessionData as { zone: number }).zone) : 0;

	// Countdown timer
	const [remaining, setRemaining] = useState(0);
	useEffect(() => {
		if (!vrfDone || farmDuration === 0) return;
		const update = () => {
			const now = Math.floor(Date.now() / 1000);
			setRemaining(Math.max(0, startedAt + farmDuration - now));
		};
		update();
		const interval = setInterval(update, 1000);
		return () => clearInterval(interval);
	}, [vrfDone, startedAt, farmDuration]);

	// Determine display state
	let statusDot = "mission-terminal__dot";
	let statusText = "Launching...";

	if (vrfDone && claimable) {
		statusDot = "mission-terminal__dot mission-terminal__dot--ready";
		statusText = "Ready";
	} else if (vrfDone && remaining > 0) {
		statusDot = "mission-terminal__dot mission-terminal__dot--farming";
		statusText = "Farming";
	} else if (vrfDone) {
		statusDot = "mission-terminal__dot mission-terminal__dot--farming";
		statusText = "Approaching";
	}

	function handleClaim() {
		claimFarm(session.sessionId);
	}

	return (
		<>
			<div className="mission-terminal__row">
				<div className="mission-terminal__cell mission-terminal__cell--ship">
					<ShipIcon tier={session.shipTier} size="sm" />
					<span className="mission-terminal__ship-id">#{session.shipTokenId.toString()}</span>
					<span className="mission-terminal__tier" style={{ color: tierColor }}>
						{tierName}
					</span>
				</div>
				<div className="mission-terminal__cell">
					{vrfDone && zone > 0 ? (
						<span className="mission-terminal__zone" style={{ color: getZoneColor(zone) }}>
							Z{zone}
						</span>
					) : (
						<span className="mission-terminal__zone--pending">---</span>
					)}
				</div>
				<div className="mission-terminal__cell">
					<span className={statusDot} />
					<span className="mission-terminal__status-text">{statusText}</span>
				</div>
				<div className="mission-terminal__cell mission-terminal__cell--mono">
					{vrfDone && remaining > 0 ? formatCountdown(remaining) : "--:--"}
				</div>
				<div className="mission-terminal__cell mission-terminal__cell--action">
					{claimable && (
						<LoadingButton
							onClick={handleClaim}
							loading={isClaiming}
							className="mission-terminal__claim-btn"
						>
							Claim
						</LoadingButton>
					)}
				</div>
			</div>

			{showClaimAnim && (
				<ClaimAnimationModal
					sessionId={session.sessionId}
					zone={zone}
					shipTier={session.shipTier}
					onClose={() => setShowClaimAnim(false)}
				/>
			)}
		</>
	);
}
